"use client"

import styled from "@emotion/styled"
import SearchIcon from "@mui/icons-material/Search"
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material"
import { useTranslations } from "next-intl"
import { useEffect, useRef, useState } from "react"
import { mediaQueries } from "ui"
import { colors, sizes } from "ui/theme/theme"
import { useJobsContext } from "../../JobsContext"

const SearchContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 380px;
  ${mediaQueries.desktop} {
    width: 404px;
  }
`

const StyledTextField = styled(TextField)`
  width: 100%;
  background-color: ${colors.white};
  border-radius: ${sizes.borderRadiusLarge};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  outline: none;
  border: none;
  .MuiOutlinedInput-input {
    border: none;
  }
  & .MuiOutlinedInput-root {
    border-radius: 24px;
    align-items: center;
  }

  & .MuiInputAdornment-root {
    color: ${colors.black};
    margin: 0;
  }

  & .MuiOutlinedInput-input {
    padding: 16px 20px;
    font-size: 16px;

    &::placeholder {
      opacity: 0.7;
    }
  }
`

const ResultsCount = styled(Typography)`
  text-align: center;
  margin-top: ${sizes.baseSingle};
  color: ${colors.white};
  font-size: 14px;
  font-weight: 800;
`

const SearchIconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`

interface JobsSearchBarProps {
  searchText?: string
}

const JobsSearchBar = ({ searchText }: JobsSearchBarProps) => {
  // Get translations
  const t = useTranslations("JobsSearch")

  // Check if JobsContext is available
  try {
    const contextTest = useJobsContext()
    if (!contextTest) {
      console.error("JobsContext is not available")
      return (
        <SearchContainer>
          <Typography color="error" align="center">
            {t("error")}
          </Typography>
        </SearchContainer>
      )
    }
  } catch (error) {
    console.error("Error accessing JobsContext:", error)
    return (
      <SearchContainer>
        <Typography color="error" align="center">
          {t("error")}
        </Typography>
      </SearchContainer>
    )
  }
  const { searchJobs, filteredJobs, allJobs, isLoading, searchQuery } =
    useJobsContext()
  const [localSearch, setLocalSearch] = useState("")
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalSearch(value)

    // Clear existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    // Set a new timeout
    debounceTimeout.current = setTimeout(() => {
      searchJobs(value)
    }, 300)
  }

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [])

  // Initialize local search state from context
  useEffect(() => {
    setLocalSearch(searchQuery)
  }, [searchQuery])

  return (
    <SearchContainer>
      <StyledTextField
        placeholder={searchText || t("placeholder")}
        variant="outlined"
        value={localSearch}
        onChange={handleSearchChange}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              {isLoading ? (
                <SearchIconWrapper>
                  <CircularProgress size={20} />
                </SearchIconWrapper>
              ) : (
                <SearchIcon />
              )}
            </InputAdornment>
          ),
        }}
      />
    </SearchContainer>
  )
}

export default JobsSearchBar
